import { type LabTestType, LabTestTypes } from "./LabTestDefinitions";

export interface FilterLeafNode {
  methodCode: string;
  label: string;
  canCreate: boolean;
}

export interface FilterTestNode {
  id: string;
  label: string;
  typeCode: string;
  typeName: string;
  sublabel?: string;
  leaves: FilterLeafNode[];
}

export interface FilterGroupNode {
  id: string;
  label: string;
  tests: FilterTestNode[];
}

const byLabel = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label);

/**
 * Builds the three-level catalogue tree (Test Group → Test Name → Standard) shown by the
 * test list filter and the Add Test modal. Hidden types/methods are excluded; methods are
 * grouped into test-name nodes by `testName ?? type.name`; groups and tests are alphabetical.
 */
export function buildLabTestFilterTree(types: readonly LabTestType[] = LabTestTypes): FilterGroupNode[] {
  const groups = new Map<string, Map<string, FilterTestNode>>();

  for (const type of types) {
    if (type.hide) continue;
    for (const method of type.types) {
      if (method.hide) continue;
      const label = method.testName ?? type.name;
      const tests = groups.get(type.group) ?? new Map<string, FilterTestNode>();
      groups.set(type.group, tests);
      const test = tests.get(label) ?? {
        id: `test:${type.code}:${label}`,
        label,
        typeCode: type.code,
        typeName: type.name,
        leaves: [],
      };
      tests.set(label, test);
      test.leaves.push({
        methodCode: method.code,
        label: method.standardLabel ?? method.standard,
        canCreate: method.canCreate,
      });
    }
  }

  return [...groups.entries()]
    .map(([label, tests]) => ({
      id: `group:${label}`,
      label,
      tests: [...tests.values()].sort(byLabel),
    }))
    .sort(byLabel);
}

const parenthetical = /\(([^)]+)\)\s*$/;

function variantLabel(test: FilterTestNode, label: string): string | null {
  const match = parenthetical.exec(label);
  if (match) return match[1];
  for (const prefix of [`${test.typeName} `, `${test.typeCode} `]) {
    if (label.startsWith(prefix)) return label.slice(prefix.length);
  }
  return null;
}

function collapseVariants(sources: readonly FilterTestNode[]): string | undefined {
  const variants: string[] = [];
  for (const source of sources) {
    for (const label of [source.label, ...source.leaves.map((leaf) => leaf.label)]) {
      const variant = variantLabel(source, label);
      if (variant && !variants.includes(variant)) variants.push(variant);
    }
  }
  return variants.length > 1 ? variants.join(", ") : undefined;
}

export function collapseTreeToTypes(tree: readonly FilterGroupNode[]): FilterGroupNode[] {
  return tree.map((group) => {
    const byTypeCode = new Map<string, FilterTestNode[]>();
    for (const test of group.tests) {
      const sources = byTypeCode.get(test.typeCode) ?? [];
      byTypeCode.set(test.typeCode, sources);
      sources.push(test);
    }

    const tests = [...byTypeCode.entries()].map(([typeCode, sources]) => ({
      id: `type:${typeCode}`,
      label: sources[0].typeName,
      typeCode,
      typeName: sources[0].typeName,
      sublabel: collapseVariants(sources),
      leaves: sources.flatMap((source) => source.leaves),
    }));

    return { ...group, tests: tests.sort(byLabel) };
  });
}

export function collectLeafCodes(nodes: readonly (FilterGroupNode | FilterTestNode)[]): string[] {
  return nodes.flatMap((node) =>
    "tests" in node
      ? node.tests.flatMap((test) => test.leaves.map((leaf) => leaf.methodCode))
      : node.leaves.map((leaf) => leaf.methodCode),
  );
}

export interface FoundLeaf {
  group: FilterGroupNode;
  test: FilterTestNode;
  leaf: FilterLeafNode;
}

/**
 * Drops non-creatable leaves, then any Test/Group left with no leaves — used by the New Test
 * dialog's picker, which (unlike the list filter) only ever offers types the user can create.
 */
export function filterCreatableTree(tree: readonly FilterGroupNode[]): FilterGroupNode[] {
  return tree
    .map((group) => ({
      ...group,
      tests: group.tests
        .map((test) => ({ ...test, leaves: test.leaves.filter((leaf) => leaf.canCreate) }))
        .filter((test) => test.leaves.length > 0),
    }))
    .filter((group) => group.tests.length > 0);
}

export type TriState = "checked" | "unchecked" | "indeterminate";

export function getTriState(selected: ReadonlySet<string>, leafCodes: readonly string[]): TriState {
  const count = leafCodes.filter((code) => selected.has(code)).length;
  if (count === 0) return "unchecked";
  return count === leafCodes.length ? "checked" : "indeterminate";
}

/** All-on → remove all given leaves from the selection; otherwise add them all. */
export function toggleLeafCodes(selected: ReadonlySet<string>, leafCodes: readonly string[]): Set<string> {
  const next = new Set(selected);
  const allSelected = leafCodes.every((code) => next.has(code));
  for (const code of leafCodes) {
    if (allSelected) next.delete(code);
    else next.add(code);
  }
  return next;
}

const matchesAll = (tokens: readonly string[], haystack: string) => {
  const text = haystack.toLowerCase();
  return tokens.every((token) => text.includes(token));
};

const narrowTests = (group: FilterGroupNode, tokens: readonly string[]) =>
  group.tests
    .map((test) => {
      const testText = `${test.label} ${test.typeCode} ${test.sublabel ?? ""}`;
      if (matchesAll(tokens, testText)) return test;
      const leaves = test.leaves.filter((leaf) => matchesAll(tokens, `${testText} ${leaf.label}`));
      return leaves.length ? { ...test, leaves } : null;
    })
    .filter((test) => test !== null);

/** Every token must match; tests are tried before the group label consumes any, so a short token narrows a test instead of being swallowed by a group word. */
export function searchFilterTree(tree: readonly FilterGroupNode[], query: string): FilterGroupNode[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return [...tree];

  const result: FilterGroupNode[] = [];
  for (const group of tree) {
    const tests = narrowTests(group, tokens);
    if (tests.length) {
      result.push({ ...group, tests });
      continue;
    }
    const groupText = group.label.toLowerCase();
    const rest = tokens.filter((token) => !groupText.includes(token));
    if (rest.length === tokens.length) continue;
    if (!rest.length) {
      result.push(group);
      continue;
    }
    const spanning = narrowTests(group, rest);
    if (spanning.length) result.push({ ...group, tests: spanning });
  }
  return result;
}

const validMethodCodes = new Set(collectLeafCodes(buildLabTestFilterTree()));

export function isValidMethodCode(code: string): boolean {
  return validMethodCodes.has(code);
}
